import { CModal } from "@coreui/react";
import { useMemo, useReducer, useState } from "react";
import DateFilters from "../../components/ui/DateFilters";
import Pagination from "../../components/ui/Pagination";
import RenderTable from "../../components/ui/RenderTable";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import { RideStatus, RideStatusLabels } from "../../helpers/enums";
import { Chip, pluralize } from "../../helpers/general";
import { useGetRidesByIds } from "../../hooks/useGetFullRides";
import { useRides } from "../../hooks/useRides";
import { general } from "../../locales/general";
import RidersTable from "../taxi/RidersTable";
import PayRideModalContent from "./PayRideModalContent";
import { cardStyle } from "../Places/PlacesTable";
import { FilterField, PillSelect } from "../Bookings/BookingFilters";

type DriverRidesProps = {
  driver: Driver | undefined;
};
const pageSize = 10;

type ModalState = {
  visible: boolean;
  target: "discount" | "bonus" | "balance" | null;
};

export default function DriverRides({ driver }: DriverRidesProps) {
  const [selectedRides, dispatch] = useReducer(
    bulkDeleteReducer,
    bulkDeleteState,
  );
  const defaultOption = useMemo(
    () => ({
      driverId: driver?.id,
    }),
    [driver?.id],
  );

  const [modalState, setModalState] = useState<ModalState>({
    visible: false,
    target: null,
  });

  function QuickFilterButton({
    label,
    count,
    active,
    color,
    onClick,
    onClear,
  }: {
    label: string;
    count?: number;
    active: boolean;
    color: "danger" | "warning" | "info";
    onClick: () => void;
    onClear: () => void;
  }) {
    return (
      <div className="d-inline-flex align-items-center gap-1">
        <button
          type="button"
          className={`btn rounded-pill d-flex align-items-center gap-2 px-3 ${
            active ? `btn-${color} text-white` : `btn-outline-${color}`
          }`}
          style={{ height: 38, fontSize: 13 }}
          onClick={onClick}
          disabled={!count && !active}
        >
          <Chip
            tone={
              active ? "neutral" : color === "danger" ? "danger" : "warning"
            }
          >
            {count ?? 0}
          </Chip>
          {label}
        </button>
        {active && (
          <button
            type="button"
            className="btn-close"
            aria-label="Effacer le filtre"
            onClick={onClear}
          />
        )}
      </div>
    );
  }

  const {
    queries,
    addQuery,
    isLoading,
    rides,
    setDates,
    clearQuickFilter,
    unpaidCounts,
    isQuickFilterActive,
    activeQuickFilter,
    handleGetUnpaidBonus,
    handleGetUnpaidCoupon,
    handleGetUnpaidBalance,
  } = useRides(defaultOption);

  const { data: allSelectedRides = [] } = useGetRidesByIds(selectedRides.ids);

  // console.log(allSelectedRides);

  const balanceAmountTotal = useMemo(() => {
    return allSelectedRides.reduce((acc: number, ride: Ride) => {
      return acc + (Number(ride.fare) || Number(ride.originalFare) || 0);
    }, 0);
  }, [allSelectedRides]);

  // Check if ALL selected rides are eligible for user balance payment
  const allSelectedRidesEligibleForBalance = useMemo(() => {
    if (!allSelectedRides.length) return false;

    return allSelectedRides.every((ride: Ride) => {
      const isUserBalancePayment = ride.paymentMethod === "user_balance";

      const isBalanceNotPaid = !ride.metaData?.userBalancePayment?.paid;

      const isCompletedAndValid =
        ride.status === RideStatus.COMPLETED &&
        ride.metaData?.validRide === true;

      return isUserBalancePayment && isBalanceNotPaid && isCompletedAndValid;
    });
  }, [allSelectedRides]);

  const discountAmountTotal = useMemo(() => {
    return allSelectedRides.reduce((acc: any, ride: Ride) => {
      const difference = Number(ride.originalFare) - Number(ride.fare);
      return acc + (difference > 0 ? difference : 0);
    }, 0);
  }, [allSelectedRides]);

  const allRidesHaveUnpaidCoupon = useMemo(() => {
    if (!allSelectedRides.length) return false;

    return allSelectedRides.every((ride: Ride) => {
      const coupon = ride.priceCalculation?.coupon;
      const metaDataCoupon = ride.metaData?.couponDiscount;

      const isApplied = coupon?.isCouponApplied === true;

      const isPaid = metaDataCoupon?.paid === true;

      return isApplied && !isPaid;
    });
  }, [allSelectedRides]);

  const allRidesHaveUnpaidBonus = useMemo(() => {
    if (!allSelectedRides.length) return false;

    return allSelectedRides.every((ride: Ride) => {
      const bonus = ride.metaData?.driverPayment;

      const isPaid = bonus?.paid === true;

      return !isPaid;
    });
  }, [allSelectedRides]);

  if (!driver) return null;

  const resetSelectedRides = () => {
    dispatch({ type: "", id: "" });
  };

  return (
    <section className="p-4">
      {/* Title + count */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <h5 className="fs-4 fw-bold mb-0">Trajets</h5>
          {rides?.meta?.totalItems !== undefined && (
            <Chip tone="warning">
              {rides.meta.totalItems}{" "}
              {pluralize("Trajet", rides.meta.totalItems)}
            </Chip>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={{ ...cardStyle, padding: "12px 16px" }} className="mb-3">
        <nav className="d-flex flex-wrap align-items-end gap-3">
          <DateFilters
            label="Date"
            onChange={setDates}
            onPeriodChange={(period) => {
              addQuery("period", period || undefined);
              resetSelectedRides();
            }}
          />

          <FilterField label="Statut">
            <PillSelect
              value={queries.status || ""}
              onChange={(v) => addQuery("status", v as RideStatus)}
              options={Object.entries(RideStatusLabels).map(([k, v]) => ({
                value: k,
                label: String(v),
              }))}
            />
          </FilterField>

          <FilterField label="Mode de paiement">
            <PillSelect
              value={queries.paymentMethod || ""}
              onChange={(v) => addQuery("paymentMethod", v as any)}
              options={[
                { value: "cash", label: "Espèces" },
                { value: "user_balance", label: "Solde utilisateur" },
              ]}
            />
          </FilterField>

          <FilterField label="Bonus payé ?">
            <PillSelect
              value={queries.payed || ""}
              onChange={(v) => addQuery("payed", v as any)}
              options={[
                { value: "true", label: "Payé" },
                { value: "false", label: "Non payé" },
              ]}
            />
          </FilterField>

          <FilterField label="Coupon payé ?">
            <PillSelect
              value={queries.couponPaid || ""}
              onChange={(v) => addQuery("couponPaid", v as any)}
              options={[
                { value: "true", label: "Payé" },
                { value: "false", label: "Non payé" },
              ]}
            />
          </FilterField>

          <FilterField label="Trajet valide ?">
            <PillSelect
              value={queries.isValidRide || ""}
              onChange={(v) => addQuery("isValidRide", v as any)}
              options={[
                { value: "true", label: "Valide" },
                { value: "false", label: "Non valide" },
                { value: "pending", label: "En attente" },
              ]}
            />
          </FilterField>

          <FilterField label="Solde utilisateur payé ?">
            <PillSelect
              value={queries.userBalancePaid || ""}
              onChange={(v) => addQuery("userBalancePaid", v as any)}
              options={[
                { value: "true", label: "Payé" },
                { value: "false", label: "Non payé" },
              ]}
            />
          </FilterField>
        </nav>
      </div>

      {/* Quick filters */}
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <QuickFilterButton
          label="Bonus impayés"
          color="danger"
          count={unpaidCounts?.bonusUnpaid}
          active={activeQuickFilter === "bonus"}
          onClick={() => {
            handleGetUnpaidBonus();
            resetSelectedRides();
          }}
          onClear={() => {
            clearQuickFilter();
            resetSelectedRides();
          }}
        />
        <QuickFilterButton
          label="Coupons impayés"
          color="warning"
          count={unpaidCounts?.couponUnpaid}
          active={activeQuickFilter === "coupon"}
          onClick={() => {
            handleGetUnpaidCoupon();
            resetSelectedRides();
          }}
          onClear={() => {
            clearQuickFilter();
            resetSelectedRides();
          }}
        />
        <QuickFilterButton
          label="Solde util. impayé"
          color="info"
          count={unpaidCounts?.balanceUnpaid}
          active={activeQuickFilter === "balance"}
          onClick={() => {
            handleGetUnpaidBalance();
            resetSelectedRides();
          }}
          onClear={() => {
            clearQuickFilter();
            resetSelectedRides();
          }}
        />

        {isQuickFilterActive && (
          <button
            type="button"
            className="btn btn-outline-secondary rounded-pill px-3 ms-auto"
            style={{ height: 38, fontSize: 13 }}
            onClick={clearQuickFilter}
          >
            Voir tous les trajets
          </button>
        )}
      </div>

      {queries.fetchAll && (
        <div className="alert alert-warning mb-3" style={{ fontSize: 13 }}>
          Affichage de <strong>tous</strong> les trajets correspondants (
          {rides?.items?.length ?? 0}). Pagination désactivée.
        </div>
      )}

      {/* Bulk actions */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        <button
          type="button"
          className="btn btn-danger text-white rounded-pill px-3"
          style={{ height: 38, fontSize: 13 }}
          disabled={selectedRides.ids.length === 0 || !allRidesHaveUnpaidCoupon}
          onClick={() =>
            setModalState({ ...modalState, visible: true, target: "discount" })
          }
        >
          Payer les frais de coupon
        </button>
        <button
          type="button"
          className="btn btn-danger text-white rounded-pill px-3"
          style={{ height: 38, fontSize: 13 }}
          disabled={
            selectedRides.ids.length === 0 ||
            selectedRides.ids.length % 10 !== 0 ||
            !allRidesHaveUnpaidBonus
          }
          onClick={() =>
            setModalState({ ...modalState, visible: true, target: "bonus" })
          }
        >
          Payer le bonus de trajet
        </button>
        <button
          type="button"
          className="btn btn-danger text-white rounded-pill px-3"
          style={{ height: 38, fontSize: 13 }}
          disabled={
            selectedRides.ids.length === 0 ||
            !allSelectedRidesEligibleForBalance
          }
          onClick={() =>
            setModalState({ ...modalState, visible: true, target: "balance" })
          }
        >
          Payer le solde de trajet
        </button>
      </div>

      {/* Table */}
      <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
        <RenderTable
          loading={isLoading}
          render={() =>
            rides?.items && rides.items.length > 0 ? (
              <RidersTable
                riders={rides.items}
                onRiderSelect={dispatch}
                selectedRides={selectedRides.ids}
                showDriver={false}
                ShowSelectCount={true}
              />
            ) : (
              <p className="text-center text-muted py-5 mb-0">
                {general.fr.noData}
              </p>
            )
          }
        />
      </div>

      {!queries.fetchAll && (
        <Pagination
          currentPage={queries.page!}
          onPageChange={(page) => addQuery("page", page)}
          siblingCount={1}
          totalCount={rides?.meta?.totalPages || 0}
          pageSize={pageSize}
        />
      )}

      <CModal
        alignment="center"
        visible={modalState.visible}
        onClose={() => setModalState({ ...modalState, visible: false })}
      >
        <PayRideModalContent
          target={modalState.target}
          initialValue={
            modalState.target === "balance"
              ? balanceAmountTotal
              : discountAmountTotal
          }
          onClose={() => {
            setModalState({ ...modalState, visible: false });
            dispatch({ type: "", id: "" });
          }}
          driverId={driver.id}
          rides={selectedRides.ids}
        />
      </CModal>
    </section>
  );
}
